import { Injectable, OnDestroy } from '@angular/core'
import { fromEvent, Subject, Subscription } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

export interface TableScrollState {
  hasHiddenContentLeft: boolean
  hasHiddenContentRight: boolean
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
}

@Injectable({
  providedIn: 'root'
})
export class TableScrollService implements OnDestroy {
  private scrollSubscriptions = new Map<string, Subscription>()
  private destroy$ = new Subject<void>()

  /**
   * Sets up scroll listener for a table container
   * @param containerId - The ID of the container element
   * @param callback - Callback function that receives scroll state updates
   * @param debounceMs - Debounce time in milliseconds (default: 100)
   * @returns A subscription that can be unsubscribed manually
   */
  setupScrollListener(
    containerId: string,
    callback: (state: TableScrollState) => void,
    debounceMs = 100
  ): Subscription | null {
    const container = document.getElementById(containerId)
    if (!container) {
      console.warn(`Table container with ID '${containerId}' not found`)
      return null
    }

    // Clean up existing subscription for this container
    this.removeScrollListener(containerId)

    const subscription = fromEvent(container, 'scroll')
      .pipe(debounceTime(debounceMs), takeUntil(this.destroy$))
      .subscribe(() => {
        const state = this.getScrollState(container)
        callback(state)
      })

    this.scrollSubscriptions.set(containerId, subscription)

    // Trigger initial state check
    setTimeout(() => {
      const state = this.getScrollState(container)
      callback(state)
    })

    return subscription
  }

  /**
   * Removes scroll listener for a specific container
   * @param containerId - The ID of the container element
   */
  removeScrollListener(containerId: string): void {
    const subscription = this.scrollSubscriptions.get(containerId)
    if (subscription) {
      subscription.unsubscribe()
      this.scrollSubscriptions.delete(containerId)
    }
  }

  /**
   * Gets the current scroll state of a container
   * @param containerIdOrElement - Container ID string or HTMLElement
   * @returns Current scroll state
   */
  getScrollState(containerIdOrElement: string | HTMLElement): TableScrollState {
    const container =
      typeof containerIdOrElement === 'string'
        ? document.getElementById(containerIdOrElement)
        : containerIdOrElement

    if (!container) {
      return {
        hasHiddenContentLeft: false,
        hasHiddenContentRight: false,
        scrollLeft: 0,
        scrollWidth: 0,
        clientWidth: 0
      }
    }

    const { scrollLeft, scrollWidth, clientWidth } = container
    const hasHiddenContentLeft = scrollLeft > 0
    const hasHiddenContentRight = scrollLeft + clientWidth < scrollWidth

    return {
      hasHiddenContentLeft,
      hasHiddenContentRight,
      scrollLeft,
      scrollWidth,
      clientWidth
    }
  }

  /**
   * Scrolls a container to the far left
   * @param containerIdOrElement - Container ID string or HTMLElement
   * @param smooth - Whether to use smooth scrolling (default: true)
   */
  scrollToLeft(containerIdOrElement: string | HTMLElement, smooth = true): void {
    const container =
      typeof containerIdOrElement === 'string'
        ? document.getElementById(containerIdOrElement)
        : containerIdOrElement

    if (container) {
      container.scrollTo({
        left: 0,
        behavior: smooth ? 'smooth' : 'auto'
      })
    }
  }

  /**
   * Scrolls a container to the far right
   * @param containerIdOrElement - Container ID string or HTMLElement
   * @param smooth - Whether to use smooth scrolling (default: true)
   */
  scrollToRight(containerIdOrElement: string | HTMLElement, smooth = true): void {
    const container =
      typeof containerIdOrElement === 'string'
        ? document.getElementById(containerIdOrElement)
        : containerIdOrElement

    if (container) {
      container.scrollTo({
        left: container.scrollWidth,
        behavior: smooth ? 'smooth' : 'auto'
      })
    }
  }

  /**
   * Scrolls a container to a specific position
   * @param containerIdOrElement - Container ID string or HTMLElement
   * @param scrollLeft - The scroll position
   * @param smooth - Whether to use smooth scrolling (default: true)
   */
  scrollToPosition(containerIdOrElement: string | HTMLElement, scrollLeft: number, smooth = true): void {
    const container =
      typeof containerIdOrElement === 'string'
        ? document.getElementById(containerIdOrElement)
        : containerIdOrElement

    if (container) {
      container.scrollTo({
        left: scrollLeft,
        behavior: smooth ? 'smooth' : 'auto'
      })
    }
  }

  /**
   * Checks if a container has horizontal scroll
   * @param containerIdOrElement - Container ID string or HTMLElement
   * @returns True if the container has horizontal scrollable content
   */
  hasHorizontalScroll(containerIdOrElement: string | HTMLElement): boolean {
    const container =
      typeof containerIdOrElement === 'string'
        ? document.getElementById(containerIdOrElement)
        : containerIdOrElement

    if (!container) return false

    return container.scrollWidth > container.clientWidth
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()

    // Clean up all subscriptions
    this.scrollSubscriptions.forEach((subscription) => subscription.unsubscribe())
    this.scrollSubscriptions.clear()
  }
}
